import React from "react";
import { useData, tail } from "../lib/store.jsx";
import {
  Kpi, Panel, Combo, LineTrend, Heatmap, DataTable, Bubble,
} from "../components/ui.jsx";
import { pct1 } from "../lib/data.js";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Clinical() {
  const { d, m, period } = useData();

  const fSorted = [...d.falls].sort(
    (a, b) => a.year - b.year || a.month_num - b.month_num);
  const combo = tail(fSorted, period || 18).map((r) => {
    const py = d.falls.find(
      (x) => x.year === r.year - 1 && x.month_num === r.month_num);
    return {
      x: `${r.month_num}/${String(r.year).slice(2)}`,
      Falls: r.count,
      "Prior year": py ? py.count : null,
    };
  });

  // Average monthly falls per year — the safety-improvement trend.
  const byYear = {};
  for (const f of d.falls) (byYear[f.year] ||= []).push(f.count);
  const yearly = Object.entries(byYear)
    .map(([y, arr]) => ({
      x: +y >= m.fallsLateYear ? `${y} YTD` : y,
      year: +y,
      "Avg monthly falls":
        +(arr.reduce((s, x) => s + x, 0) / arr.length).toFixed(2),
    }))
    .sort((a, b) => a.year - b.year);

  // Falls heatmap grid — year × month.
  const years = [...new Set(d.falls.map((f) => f.year))].sort((a, b) => b - a);
  const rows = years.map((y) => ({ key: y, label: String(y),
    rowFull: String(y) }));
  const cols = MON.map((mn, i) => ({ key: i + 1, label: mn }));
  const fIdx = {};
  for (const f of d.falls) (fIdx[f.year] ||= {})[f.month_num] = f.count;
  const maxFalls = Math.max(...d.falls.map((f) => f.count), 1);

  // Falls by month × year matrix table.
  const fm2 = m.fallsMatrix;
  const matCols = [{ key: "month", label: "Month" },
    ...fm2.years.map((y) => ({ key: String(y.year),
      label: String(y.year), num: true }))];
  const ytdKeys = fm2.years.filter((y) => y.ytd).map((y) => String(y.year));
  const matRows = fm2.rows.map((r) => {
    const row = { month: r.month };
    for (const y of fm2.years) row[String(y.year)] = r.cells[y.year];
    return row;
  });
  const totalRow = { month: "Total" };
  const avgRow = { month: "Average" };
  for (const y of fm2.years) {
    totalRow[String(y.year)] = y.total;
    avgRow[String(y.year)] = y.avg;
  }
  matRows.push(totalRow, avgRow);

  // Seasonality bubble — average falls by calendar month, last three years.
  const season = m.fallsSeasonality;

  const fm = m.fLast;
  const redFull = m.fallsRedPct;
  const redYtd = m.fallsRedYtdPct;

  return (
    <>
        <Kpi label="Falls (latest month)" color="#ec9a74" value={fm ? fm.count : 0}
          fmt={(x) => Math.round(x)}
          meta={fm ? `${fm.month_num}/${fm.year}` : ""}
          badge={m.fallsSpike ? { kind: "amber", text: "⚠ spike" } : null} />
        <Kpi label={`Falls Avg ${m.fallsYear}`} color="#d9b892"
          value={m.fallsAvg2026 == null ? 0 : m.fallsAvg2026}
          fmt={(x) => x.toFixed(1)} meta="per month, year to date" />
        <Kpi label={`Falls ${m.fallsYear} YTD`} color="#f3ece2"
          value={m.fallsYtd2026 || 0} fmt={(x) => Math.round(x)}
          meta="total this year" />
        <Kpi label="Falls Reduction" color="#c8e64a"
          value={redFull == null ? 0 : Math.abs(redFull)}
          fmt={pct1}
          badge={redFull == null ? null : {
            kind: redFull <= 0 ? "up" : "down",
            text: `${redFull <= 0 ? "▼" : "▲"}` }}
          meta={`${m.fallsBaseYear}→${m.fallsRedFullYear}`
            + (redYtd == null ? ""
              : ` · ${Math.abs(redYtd).toFixed(0)}% to ${m.fallsLateYear} YTD`)}
        />
        <Panel className="s6" title="Falls trend"
          sub={`average monthly falls per year${redFull == null ? ""
            : ` — ${Math.abs(redFull).toFixed(0)}% lower than ${m.fallsBaseYear}`}`}>
          <LineTrend data={yearly} x="x"
            lines={[{ key: "Avg monthly falls", name: "Avg monthly falls" }]}
            h={300} />
        </Panel>
        <Panel className="s6" title="Falls vs prior year"
          sub="current bars · prior-year line (ghost)">
          <Combo data={combo} x="x" bar={{ key: "Falls", name: "Falls" }}
            line={[{ key: "Prior year", name: "Prior year",
              dashed: true }]} h={300} />
        </Panel>
        <Panel className="s12" title="Falls by month & year"
          sub="full record with annual totals and averages">
          <DataTable columns={matCols} rows={matRows}
            ytdKeys={ytdKeys} emphasize={["Total", "Average"]} />
        </Panel>
        <Panel className="s7" title="Falls heatmap" sub="year × month — safety pattern">
          <Heatmap rows={rows} cols={cols}
            value={(y, mo) => fIdx[y]?.[mo] ?? null}
            min={0} max={maxFalls} unit=" falls" showCellValue />
        </Panel>
        <Panel className="s5" title="Falls seasonality"
          sub={`average falls by calendar month, ${m.seasonFrom}–${String(m.seasonFrom + 2).slice(2)} to date — when they cluster`}>
          <Bubble data={season} h={300} color="#d9b892"
            yLabel="Average falls" refY={m.seasonMed} refLabel={`median ${m.seasonMed}`}
            xTickFormatter={(mn) => MON[mn - 1]}
            tipFmt={(v, nm, it) => [`${it.payload.y} falls on average`
              + ` (${it.payload.total} over ${it.payload.n} yrs)`,
              it.payload.month]} />
        </Panel>
    </>
  );
}
